import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DEFAULT_SESSION, normalizeSession, patchSession, projectDrop, scanActive, tankKey } from "@/lib/tank";
import { getChart, projectDropChart } from "@/lib/chart";

const SessionContext = createContext(null);
const LOCAL_KEY = "fhp-session-v1";

const readLocal = () => {
  try { return normalizeSession(JSON.parse(localStorage.getItem(LOCAL_KEY))); } catch { return DEFAULT_SESSION; }
};

export function SessionProvider({ children }) {
  const { user } = useAuth();
  const [session, setSession] = useState(readLocal);
  const [ready, setReady] = useState(false);
  const timer = useRef(null);
  const dirty = useRef(false);
  const uid = user ? user.user_id : null;

  useEffect(() => {
    if (user === null) return;
    setReady(false);
    if (!uid) { setReady(true); return; }
    dirty.current = false;
    api.getSession()
      .then((s) => s && !dirty.current && setSession(normalizeSession(s)))
      .catch(() => {})
      .finally(() => setReady(true));
  }, [uid, user]);

  useEffect(() => {
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify(session)); } catch { /* storage full */ }
    if (!ready || !uid) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => api.putSession(session).catch(() => {}), 400);
  }, [session, ready, uid]);

  const patch = useCallback((p) => { dirty.current = true; setSession((s) => patchSession(s, p)); }, []);
  const applyTank = useCallback((capacity, diameter, ends, startInches, dropGallons) => {
    dirty.current = true;
    setSession((s) =>
      patchSession(s, {
        capacity, diameter, ends,
        ...(startInches != null ? { startInches } : {}),
        ...(dropGallons != null ? { dropGallons } : {}),
      })
    );
  }, []);

  const [chartTick, setChartTick] = useState(0);
  useEffect(() => {
    const bump = () => setChartTick((n) => n + 1);
    window.addEventListener("charts-changed", bump);
    return () => window.removeEventListener("charts-changed", bump);
  }, []);
  const result = useMemo(() => {
    const onSite = session.activeSiteId && session.activeKey === tankKey(session);
    const rows = onSite ? getChart(session.activeSiteId, session.activeLabel) : null;
    if (rows) return projectDropChart(session.startInches, session.dropGallons, rows, session.capacity);
    const consoleGal = scanActive(session) && session.scan.gallons > 0 && session.scan.gallons <= session.capacity ? session.scan.gallons : null;
    return { ...projectDrop(session.startInches, session.dropGallons, session.diameter, session.capacity, session.ends, consoleGal), source: consoleGal != null ? "console" : "geometry" };
  }, [session, chartTick]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SessionContext.Provider value={{ session, ready, result, patch, applyTank }}>{children}</SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within SessionProvider");
  return ctx;
}
